import * as React from "react";

import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type DialogHeadingProps = Omit<
  React.ComponentProps<typeof DialogHeader>,
  "children" | "title"
> & {
  /** モーダルの主題。常に DialogTitle として読み上げられます。 */
  title: React.ReactNode;
  /** タイトルを補足する短い説明。 */
  description?: React.ReactNode;
  /** タイトルの左に表示する装飾アイコン。アクセシビリティ属性は呼び出し元で指定します。 */
  icon?: React.ReactNode;
  /** タイトルの横に表示する状態ラベルなど。 */
  badge?: React.ReactNode;
  titleClassName?: string;
  descriptionClassName?: string;
};

/**
 * フォームや詳細モーダルで共通して使う、左揃えの見出しです。
 *
 * DialogContent の標準の閉じるボタンと重ならない右余白だけを持ち、
 * 背景・枠線・内側の余白は呼び出し側で必要な場合にだけ追加します。
 * アイコンはタイトルだけと組み合わせ、説明文は下段で本文と左端を揃えます。
 */
function DialogHeading({
  title,
  description,
  icon,
  badge,
  className,
  titleClassName,
  descriptionClassName,
  ...props
}: DialogHeadingProps) {
  const accessibleDescription =
    description ??
    (typeof title === "string"
      ? `${title}の内容を確認してください。`
      : "ダイアログの内容を確認してください。");

  return (
    <DialogHeader className={cn("gap-0 pr-14 text-left", className)} {...props}>
      <div
        data-slot="dialog-title-row"
        className={cn("flex min-w-0 gap-3", description ? "items-end" : "items-center")}
      >
        {icon ? (
          <div className="text-primary shrink-0 [&_svg]:size-5">{icon}</div>
        ) : null}

        <div className={cn("flex min-w-0 flex-1 gap-x-2", description ? "items-end" : "items-center")}>
          <DialogTitle
            className={cn("min-w-0 text-pretty break-words", titleClassName)}
          >
            {title}
          </DialogTitle>
          {badge ? <div className="shrink-0">{badge}</div> : null}
        </div>
      </div>

      <DialogDescription
        className={cn(
          !description && "sr-only",
          "text-pretty break-words",
          descriptionClassName
        )}
      >
        {accessibleDescription}
      </DialogDescription>
    </DialogHeader>
  );
}

export { DialogHeading };
export type { DialogHeadingProps };
